// src/modules/app-views/toolbox/pages/Ai/components/InputForm.jsx
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useCallback, useState } from 'react';
import { Flex, Row, Col, Select, Tag, Tooltip, Alert, Typography, theme } from 'antd';
import { SyncOutlined } from '@ant-design/icons';
import SimpleFormProvider from 'components/SimpleComponents/SimpleFormProvider';
import SimpleInput from 'components/SimpleComponents/SimpleInput';
import SimpleButton from 'components/SimpleComponents/SimpleButton';
import translateX from 'utils/translateX';
import aiIcon from 'assets/images/ai.png';

// *******************************************************************************
// MAIN COMPONENT
// *******************************************************************************
/**
 * InputForm - Form for tool description input
 */
const InputForm = ({
  formRef,
  isChatMode = false,
  lastFormValues = null,
  selectedModel,
  aiModels = [],
  creditsInfo = {},
  isProcessing = false,
  isMobile = false,
  onModelChange,
  onFinish,
  onBackToWelcome,
  onOpenCreditsDrawer,
}) => {
  // ---------------------------------------------------------------------------
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ---------------------------------------------------------------------------
  const { token } = theme.useToken();

  // ---------------------------------------------------------------------------
  // 2. HOOKS & REFS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 3. LOCAL VARIABLES I
  // ---------------------------------------------------------------------------
  const {
    requiredCredits = 0,
    availableCredits = 0,
    hasSufficientCredits = true,
    isLoading: isLoadingCredits = false,
  } = creditsInfo;

  const isConfirmDisabled = isProcessing || isLoadingCredits || !hasSufficientCredits;

  const aiModelOptions = aiModels.map(model => ({
    value: model.alias,
    label: model.name,
  }));

  // ---------------------------------------------------------------------------
  // 4. LOCAL STATE
  // ---------------------------------------------------------------------------
  const [selectOpen, setSelectOpen] = useState(false);

  // ---------------------------------------------------------------------------
  // 5. EFFECTS
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 6. HANDLERS
  // ---------------------------------------------------------------------------
  const handleKeyDown = useCallback(
    e => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        formRef.current?.submitForm();
      }
    },
    [formRef]
  );

  const renderModelLabel = useCallback(
    ({ value, label }) => {
      const model = aiModels.find(m => m.alias === value);
      if (!model) return <span>{label}</span>;

      return (
        <Flex align="center" gap={8} style={{ width: '100%' }}>
          <span>{label}</span>
          {model.recommended && (
            <Tag
              color="green"
              style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
            >
              {translateX('recommended')}
            </Tag>
          )}
          {model.tier === 'premium' && !model.recommended && (
            <Tag
              color="orange"
              style={{ margin: 0, fontSize: 10, lineHeight: '16px', padding: '0 4px' }}
            >
              {translateX('premium')}
            </Tag>
          )}
        </Flex>
      );
    },
    [aiModels]
  );

  const renderModelOption = useCallback(
    option => renderModelLabel(option.data),
    [renderModelLabel]
  );

  const handleModelChange = useCallback(
    value => {
      setSelectOpen(false);
      onModelChange?.(value);
    },
    [onModelChange]
  );

  // ---------------------------------------------------------------------------
  // 7. LOCAL VARIABLES II
  // ---------------------------------------------------------------------------

  // ---------------------------------------------------------------------------
  // 8. UI
  // ---------------------------------------------------------------------------
  return (
    <SimpleFormProvider
      formRef={formRef}
      onFinish={onFinish}
      showInfoMessage={false}
      showWarningMessage={false}
      ignoreResetFormOnSubmit
      initFocus="toolDescription"
      initialValues={lastFormValues}
    >
      {isChatMode ? (
        // Chat mode: tool description input + controls
        <>
          <Row gutter={[12, 0]}>
            <Col span={24}>
              <div onKeyDown={handleKeyDown}>
                <SimpleInput
                  name="toolDescription"
                  label={translateX('insert_new_tool')}
                  placeholder={translateX('tool_description_placeholder')}
                  required
                  style={{ height: 40 }}
                />
              </div>
            </Col>
          </Row>

          <Flex
            gap={isMobile ? 20 : 12}
            wrap="wrap"
            align="flex-end"
            vertical={isMobile}
            style={{ marginTop: 8 }}
          >
            {/* AI Engine Select */}
            <div
              style={{
                flex: isMobile ? undefined : 1,
                minWidth: isMobile ? undefined : 200,
                width: isMobile ? '100%' : undefined,
              }}
            >
              <Tooltip title={translateX('ai_engine')}>
                <Select
                  disabled
                  options={aiModelOptions}
                  value={aiModels.length > 0 ? selectedModel : undefined}
                  open={selectOpen}
                  onOpenChange={setSelectOpen}
                  onChange={handleModelChange}
                  optionRender={renderModelOption}
                  labelRender={renderModelLabel}
                  placeholder={translateX('loading')}
                  style={{ width: '100%', maxWidth: 320, height: 40 }}
                />
              </Tooltip>
              {isMobile && (
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 12, marginTop: 4, display: 'block', textAlign: 'center' }}
                >
                  {translateX('ai_cataloging_cost_credits', { credits: requiredCredits })}
                </Typography.Text>
              )}
            </div>

            {/* Buttons */}
            <Flex
              gap={8}
              justify={isMobile ? 'center' : undefined}
              style={{ width: isMobile ? '100%' : undefined }}
            >
              <SimpleButton
                type="primary"
                title={translateX('confirm')}
                icon={
                  <img
                    src={aiIcon}
                    alt=""
                    style={{
                      width: 16,
                      height: 16,
                      filter: isConfirmDisabled ? 'none' : 'invert(1)',
                    }}
                  />
                }
                disabled={isConfirmDisabled}
                style={{ width: 120 }}
              />
            </Flex>
          </Flex>

          {/* Cost info - desktop */}
          {!isMobile && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, marginTop: 8, marginBottom: 12, display: 'block' }}
            >
              {translateX('ai_cataloging_cost_credits', { credits: requiredCredits })}
            </Typography.Text>
          )}

          {/* Credits balance Alert */}
          {!hasSufficientCredits && !isLoadingCredits ? (
            <Alert
              type="warning"
              className="mt-4"
              showIcon
              style={{ marginBottom: 12 }}
              title={
                <span>
                  {translateX('your_balance_is_plain')} <strong>{availableCredits}</strong>{' '}
                  {translateX('credits')}.{' '}
                  <a onClick={onOpenCreditsDrawer} style={{ cursor: 'pointer' }}>
                    {translateX('click_to_acquire_credits')}
                  </a>
                </span>
              }
            />
          ) : (
            <Alert
              type="info"
              className="mt-4"
              showIcon
              style={{ marginBottom: 12 }}
              title={
                <span>
                  {translateX('your_current_balance_is', {
                    credits: (
                      <strong>{isLoadingCredits ? <SyncOutlined spin /> : availableCredits}</strong>
                    ),
                  })}{' '}
                  <a onClick={onOpenCreditsDrawer} style={{ cursor: 'pointer' }}>
                    {translateX('click_to_acquire_credits')}
                  </a>
                </span>
              }
            />
          )}
        </>
      ) : (
        // Empty state: initial form layout
        <>
          <Row gutter={[12, 0]}>
            <Col xs={24}>
              <div onKeyDown={handleKeyDown}>
                <SimpleInput
                  name="toolDescription"
                  label={translateX('tool_description')}
                  placeholder={translateX('tool_description_placeholder')}
                  required
                  style={{ height: 40 }}
                />
              </div>
            </Col>
          </Row>

          <Flex
            vertical={isMobile}
            justify={isMobile ? 'center' : 'space-between'}
            align="center"
            gap={isMobile ? 12 : 0}
            style={{ marginTop: 8, marginBottom: 12 }}
          >
            {/* AI Engine Select */}
            <Tooltip title={translateX('ai_engine')}>
              <Select
                disabled
                options={aiModelOptions}
                value={aiModels.length > 0 ? selectedModel : undefined}
                open={selectOpen}
                onOpenChange={setSelectOpen}
                onChange={handleModelChange}
                optionRender={renderModelOption}
                labelRender={renderModelLabel}
                placeholder={translateX('loading')}
                style={{ width: isMobile ? '100%' : 320, height: 40 }}
              />
            </Tooltip>

            {isMobile && (
              <Typography.Text type="secondary" style={{ fontSize: 12, textAlign: 'center' }}>
                {translateX('ai_cataloging_cost_credits', { credits: requiredCredits })}
              </Typography.Text>
            )}

            {/* Confirm button */}
            <SimpleButton
              type="primary"
              title={translateX('confirm')}
              icon={
                <img
                  src={aiIcon}
                  alt=""
                  style={{
                    width: 16,
                    height: 16,
                    filter: isConfirmDisabled ? 'none' : 'invert(1)',
                  }}
                />
              }
              disabled={isConfirmDisabled}
              style={{ width: 140, marginLeft: isMobile ? 0 : 12 }}
            />
          </Flex>

          {/* Cost info - desktop */}
          {!isMobile && (
            <Typography.Text
              type="secondary"
              style={{ fontSize: 12, marginTop: 8, marginBottom: 12, display: 'block' }}
            >
              {translateX('ai_cataloging_cost_credits', { credits: requiredCredits })}
            </Typography.Text>
          )}

          {/* Credits balance Alert */}
          {!hasSufficientCredits && !isLoadingCredits ? (
            <Alert
              type="warning"
              className="mt-4"
              showIcon
              title={
                <span>
                  {translateX('your_balance_is_plain')} <strong>{availableCredits}</strong>{' '}
                  {translateX('credits')}.{' '}
                  <a onClick={onOpenCreditsDrawer} style={{ cursor: 'pointer' }}>
                    {translateX('click_to_acquire_credits')}
                  </a>
                </span>
              }
            />
          ) : (
            <Alert
              type="info"
              className="mt-4"
              showIcon
              title={
                <span>
                  {translateX('your_current_balance_is', {
                    credits: (
                      <strong>{isLoadingCredits ? <SyncOutlined spin /> : availableCredits}</strong>
                    ),
                  })}{' '}
                  <a onClick={onOpenCreditsDrawer} style={{ cursor: 'pointer' }}>
                    {translateX('click_to_acquire_credits')}
                  </a>
                </span>
              }
            />
          )}
        </>
      )}
    </SimpleFormProvider>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default InputForm;
